import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import useAdminReturns from "../../../hooks/useAdminReturns";

/**
 * Return Review Form component for admin interface
 * Allows admins to review return requests and approve or reject items
 *
 * @param {Object} props - Component props
 * @param {Object} props.returnDetail - The return request details
 * @returns {JSX.Element} The ReturnReviewForm component
 */
const ReturnReviewForm = ({ returnDetail }) => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("returns");
  const { reviewReturn, actionStatus, actionError } = useAdminReturns();

  // Initialize form data with decisions for each return item
  const [formData, setFormData] = useState({
    itemDecisions:
      returnDetail?.items?.map((item) => ({
        returnItemId: item.id,
        adminDecision: "approved", // Default to approved
        adminQuantity: item.quantity, // Default to requested quantity
        adminNotes: "",
      })) || [],
    globalAdminNotes: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const handleItemDecisionChange = (itemIndex, field, value) => {
    setFormData((prevData) => {
      const updatedDecisions = [...prevData.itemDecisions];
      updatedDecisions[itemIndex] = {
        ...updatedDecisions[itemIndex],
        [field]: value,
      };

      // If changing decision to rejected, set quantity to 0
      if (field === "adminDecision" && value === "rejected") {
        updatedDecisions[itemIndex].adminQuantity = 0;
      }

      // If changing decision to approved, reset quantity to requested amount
      if (field === "adminDecision" && value === "approved") {
        const requestedQty = returnDetail.items[itemIndex].quantity;
        updatedDecisions[itemIndex].adminQuantity = requestedQty;
      }

      return {
        ...prevData,
        itemDecisions: updatedDecisions,
      };
    });
  };

  const handleNotesChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      globalAdminNotes: e.target.value,
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Check if at least one decision is made
    if (!formData.itemDecisions || formData.itemDecisions.length === 0) {
      errors.general = t("admin.detail.forms.returnReview.errors.noItems");
    }

    // Check individual item decisions
    formData.itemDecisions.forEach((decision, index) => {
      if (!decision.adminDecision) {
        errors[`item_${index}_decision`] = t(
          "admin.detail.forms.returnReview.errors.decisionRequired"
        );
      }

      if (decision.adminDecision === "approved") {
        const qty = parseInt(decision.adminQuantity, 10);
        const maxQty = returnDetail.items[index].quantity;

        if (isNaN(qty) || qty < 0 || qty > maxQty) {
          errors[`item_${index}_quantity`] = t(
            "admin.detail.forms.returnReview.errors.quantityRange",
            { maxQty }
          );
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    reviewReturn({
      returnId: returnDetail.id,
      itemDecisions: formData.itemDecisions,
      globalAdminNotes: formData.globalAdminNotes,
    });
  };

  const isLoading = actionStatus === "loading";

  if (!returnDetail || !returnDetail.items || returnDetail.items.length === 0) {
    return (
      <Alert severity="warning">
        {t("admin.detail.forms.returnReview.noItemsToReview")}
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError?.message || tCommon("status.error")}
        </Alert>
      )}

      {formErrors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formErrors.general}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("admin.detail.forms.returnReview.table.product")}
              </TableCell>
              <TableCell align="center">
                {t("admin.detail.forms.returnReview.table.qtyRequested")}
              </TableCell>
              <TableCell>
                {t("admin.detail.forms.returnReview.table.decision")}
              </TableCell>
              <TableCell align="center">
                {t("admin.detail.forms.returnReview.table.approvedQty")}
              </TableCell>
              <TableCell>
                {t("admin.detail.forms.returnReview.table.itemNotes")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {returnDetail.items.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>
                  {item.orderItem?.product?.name ||
                    tCommon("status.notApplicable")}
                  <Typography variant="caption" display="block">
                    {t("admin.detail.forms.returnReview.table.reason")}:{" "}
                    {item.reason ||
                      returnDetail.reason ||
                      tCommon("status.notApplicable")}
                  </Typography>
                </TableCell>
                <TableCell align="center">{item.quantity}</TableCell>
                <TableCell>
                  <FormControl
                    error={!!formErrors[`item_${index}_decision`]}
                    size="small"
                    fullWidth
                  >
                    <RadioGroup
                      row
                      value={formData.itemDecisions[index].adminDecision}
                      onChange={(e) =>
                        handleItemDecisionChange(
                          index,
                          "adminDecision",
                          e.target.value
                        )
                      }
                    >
                      <FormControlLabel
                        value="approved"
                        control={<Radio />}
                        label={t(
                          "admin.detail.forms.returnReview.decisions.approve"
                        )}
                      />
                      <FormControlLabel
                        value="rejected"
                        control={<Radio />}
                        label={t(
                          "admin.detail.forms.returnReview.decisions.reject"
                        )}
                      />
                    </RadioGroup>
                    {formErrors[`item_${index}_decision`] && (
                      <FormHelperText>
                        {formErrors[`item_${index}_decision`]}
                      </FormHelperText>
                    )}
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    inputProps={{
                      min: 0,
                      max: item.quantity,
                      style: { textAlign: "center" },
                    }}
                    value={formData.itemDecisions[index].adminQuantity}
                    onChange={(e) =>
                      handleItemDecisionChange(
                        index,
                        "adminQuantity",
                        parseInt(e.target.value, 10)
                      )
                    }
                    disabled={
                      formData.itemDecisions[index].adminDecision === "rejected"
                    }
                    error={!!formErrors[`item_${index}_quantity`]}
                    helperText={formErrors[`item_${index}_quantity`]}
                    sx={{ width: "80px" }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t(
                      "admin.detail.forms.returnReview.itemNotesPlaceholder"
                    )}
                    value={formData.itemDecisions[index].adminNotes}
                    onChange={(e) =>
                      handleItemDecisionChange(
                        index,
                        "adminNotes",
                        e.target.value
                      )
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            name="globalAdminNotes"
            label={t("admin.detail.forms.returnReview.globalNotesLabel")}
            multiline
            rows={3}
            value={formData.globalAdminNotes}
            onChange={handleNotesChange}
            fullWidth
            placeholder={t(
              "admin.detail.forms.returnReview.globalNotesPlaceholder"
            )}
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ mr: 1 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                {tCommon("status.submitting")}
              </>
            ) : (
              t("admin.detail.forms.returnReview.submitButton")
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReturnReviewForm;
